import { Document, Page, Text, View, StyleSheet, pdf } from "@react-pdf/renderer";
import moment from "moment";
import { SignatureCertificate, computeContentHash } from "./signatureCertificate";
import { buildAchRows, buildCheckRows } from "../components/admin/templates/defaults/manufacturerBankingDefault";
import { isPricingScheduleConfigured } from "../components/admin/templates/defaults/dmePricingDefault";

// Re-exported so the "omit rather than embed another org's data" gating in
// psaToPdf is unit-testable at this module's own boundary — the renderers
// close over psaToPdf's `styles` and cannot be reached directly.
export { buildAchRows, buildCheckRows, isPricingScheduleConfigured };

export const PDF_BOLD_START = "__PDF_BOLD_START__";
export const PDF_BOLD_END = "__PDF_BOLD_END__";
const BOLD_MARKER_REGEX = new RegExp(`(${PDF_BOLD_START}|${PDF_BOLD_END})`);

// Export a note to PDF with styling and download
// In file CSS for easy format adjusting
export async function noteToPdf({ patient, createdOn, visitTime, plainText }) {
  const styles = StyleSheet.create({
    page: { padding: 14, fontSize: 12, lineHeight: 1 },
    patientFirstName: {
      fontSize: 12,
      color: "#1e70e8",
      fontWeight: "bold",
      marginBottom: 2,
    },
    sectionTitle: {
      textAlign: "left",
      fontWeight: "bold",
      fontSize: 12,
      marginBottom: 8,
      marginTop: 8,
    },
    bodyText: { fontSize: 10, paddingLeft: 12 },
    bulletList: { marginLeft: 8, marginBottom: 1 },
    bulletItem: { flexDirection: "row", marginBottom: 2 },
    bulletPoint: { width: 10, fontSize: 10 },
    bulletText: { fontSize: 10, flex: 1 },
    boldText: { fontWeight: "bold" },
  });

  const getPatientFullName = (user) => {
    const first = user?.firstName?.trim();
    const last = user?.lastName?.trim();
    if (first || last) return [first, last].filter(Boolean).join(" ");
    return user?.name?.trim() || "Patient";
  };

  const patientFullName = getPatientFullName(patient);
  // const title = patientFullName;
  const subtitle = `Encounter Note • ${visitTime ? moment(visitTime).format("MM/DD/YYYY HH:mm") : moment(createdOn).format("MM/DD/YYYY HH:mm")}`;

  // Helper to parse plainText into sections and lists
  function parseNoteToElements(text) {
    const lines = text.split(/\r?\n/);
    const elements = [];
    let inList = false;
    let listItems = [];

    const renderLine = (value, key, style) => (
      <Text style={style} key={key}>
        {tokenizeBoldSegments(value).map((segment, idx) =>
          segment.bold ? (
            <Text key={`${key}-bold-${idx}`} style={styles.boldText}>
              {segment.text}
            </Text>
          ) : (
            segment.text
          )
        )}
      </Text>
    );

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <View style={styles.bulletList} key={elements.length + "-list"}>
            {listItems.map((item, i) => {
              const text = item.trim().replace(/^[-•\d.\s]+/, "");
              return (
                <View style={styles.bulletItem} key={i}>
                  <Text style={styles.bulletPoint}>•</Text>
                  {renderLine(text, `${elements.length}-bullet-${i}`, styles.bulletText)}
                </View>
              );
            })}
          </View>
        );
        listItems = [];
      }
    };

    const sectionWithColon = /^([\p{Lu}][\p{L}0-9 ./-]+:)$/u;
    const allUppercaseWords = /^[\p{Lu}][\p{Lu}\s]*$/u;

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      const plainTrimmed = stripBoldMarkers(trimmed);
      // Section title: line ends with a colon, or is all uppercase, or surrounded by === or ---
      if (/^(={2,}|-{2,})$/.test(plainTrimmed)) {
        // skip separator lines
        flushList();
        return;
      }
      if (plainTrimmed.length === 0) {
        flushList();
        elements.push(<Text key={elements.length + "-spacer"} style={{ marginBottom: 8 }}></Text>);
        return;
      }
      if (sectionWithColon.test(plainTrimmed) || allUppercaseWords.test(plainTrimmed)) {
        flushList();
        elements.push(
          <Text style={styles.sectionTitle} key={elements.length + "-section"}>
            {plainTrimmed.replace(/:$/, "")}
          </Text>
        );
        return;
      }
      // Bulleted or dashed list: starts with dash, bullet, or number
      if (/^[-•\d.]+\s+/.test(plainTrimmed)) {
        inList = true;
        listItems.push(trimmed);
        return;
      }
      // If we were in a list and now not, flush
      if (inList) {
        flushList();
        inList = false;
      }
      // Normal body text
      elements.push(renderLine(trimmed, elements.length + "-body", styles.bodyText));
    });
    flushList();
    return elements;
  }

  const NotePDF = () => (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <Text style={styles.patientFirstName}>{patientFullName}</Text>
        <Text style={styles.bodyText}>{subtitle}</Text>
        {parseNoteToElements(plainText)}
      </Page>
    </Document>
  );

  // pdf file name format
  const filenameSafe = patientFullName
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "_");
  const fileName = `${filenameSafe}_note_${moment(createdOn).format("YYYYMMDD_HHmm")}.pdf`;

  const blob = await pdf(<NotePDF />).toBlob();
  const url = URL.createObjectURL(blob);

  const base64 = await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64data = reader.result.split(",")[1];
      resolve(base64data);
    };
    reader.readAsDataURL(blob);
  });

  return { blob, url, fileName, base64 };
}

function stripBoldMarkers(value = "") {
  if (!value) return "";
  return value.replaceAll(PDF_BOLD_START, "").replaceAll(PDF_BOLD_END, "");
}

function tokenizeBoldSegments(value = "") {
  if (!value) return [{ text: "", bold: false }];
  const tokens = value.split(BOLD_MARKER_REGEX);
  let isBold = false;
  const segments = [];

  tokens.forEach((token) => {
    if (!token) return;
    if (token === PDF_BOLD_START) {
      isBold = true;
    } else if (token === PDF_BOLD_END) {
      isBold = false;
    } else {
      segments.push({ text: token, bold: isBold });
    }
  });

  return segments.length ? segments : [{ text: "", bold: false }];
}

// Shared helper: build the PDF blob, then either trigger a download
// (default) or return a `{ blob, url, fileName }` handle so a caller can
// embed the PDF in an iframe for inline preview. Callers that opt into
// preview mode are responsible for revoking the object URL when done.
function deliverPdf({ blob, fileName, asBlob }) {
  const url = URL.createObjectURL(blob);
  if (asBlob) return { blob, url, fileName };
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
  return undefined;
}

async function documentToPdf({
  contentElement,
  fileName,
  signedDate,
  signedVersion,
  signerName,
  signerEmail,
  signerTitle,
  organizationName,
  documentTitle,
  auditRecord,
  asBlob,
}) {
  if (!contentElement) {
    console.error("No content element provided for PDF export");
    return undefined;
  }

  const styles = StyleSheet.create({
    page: { padding: 50, fontSize: 11, lineHeight: 1.5, fontFamily: "Helvetica" },
    h1: { fontSize: 22, fontFamily: "Helvetica-Bold", textAlign: "center", marginBottom: 20, marginTop: 10 },
    h2: { fontSize: 16, fontFamily: "Helvetica-Bold", marginTop: 20, marginBottom: 10 },
    h3: { fontSize: 13, fontFamily: "Helvetica-Bold", marginTop: 14, marginBottom: 6 },
    p: { marginBottom: 10, textAlign: "left" },
    section: { marginTop: 16 },
    bold: { fontFamily: "Helvetica-Bold" },
    list: { marginBottom: 10, marginLeft: 12 },
    li: { marginBottom: 4, marginLeft: 4, textAlign: "left" },
    warning: {
      marginBottom: 10,
      padding: 10,
      backgroundColor: "#fff3cd",
      borderLeftWidth: 3,
      borderLeftColor: "#f0ad4e",
      fontFamily: "Helvetica-Bold",
    },
  });

  function parseHtmlToReactPdf(element, keyPrefix = "0") {
    const elements = [];
    let childIndex = 0;

    const processNode = (node) => {
      const key = `${keyPrefix}-${childIndex++}`;

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
          return text;
        }
        return null;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return null;

      if (node.classList?.contains("pdf-hide") || (typeof node.className === "string" && node.className.includes("pdf-hide"))) {
        return null;
      }

      const tagName = node.tagName.toLowerCase();
      const children = Array.from(node.childNodes)
        .map((child, i) => processNode(child))
        .filter((c) => c !== null);

      switch (tagName) {
        case "h1":
          return (
            <Text style={styles.h1} key={key}>
              {children}
            </Text>
          );
        case "h2":
          return (
            <Text style={styles.h2} key={key}>
              {children}
            </Text>
          );
        case "h3":
          return (
            <Text style={styles.h3} key={key}>
              {children}
            </Text>
          );
        case "p":
          return (
            <Text style={styles.p} key={key}>
              {children}
            </Text>
          );
        case "strong":
        case "b":
          return (
            <Text style={styles.bold} key={key}>
              {children}
            </Text>
          );
        case "em":
        case "i":
          return <Text key={key}>{children}</Text>;
        case "br":
          return <Text key={key}>{"\n"}</Text>;
        case "ul":
        case "ol":
          return (
            <View style={styles.list} key={key}>
              {children}
            </View>
          );
        case "li":
          return (
            <Text style={styles.li} key={key}>
              {children}
            </Text>
          );
        case "div":
          if (node.className?.includes("section")) {
            return (
              <View style={styles.section} key={key}>
                {children}
              </View>
            );
          }
          if (node.className?.includes("warning")) {
            return (
              <Text style={styles.warning} key={key}>
                {children}
              </Text>
            );
          }
          if (children.some((c) => typeof c === "string")) {
            return (
              <Text style={styles.p} key={key}>
                {children}
              </Text>
            );
          }
          return <View key={key}>{children}</View>;
        default:
          if (children.some((c) => typeof c === "string")) {
            return <Text key={key}>{children}</Text>;
          }
          if (children.length > 0) {
            return <View key={key}>{children}</View>;
          }
          return null;
      }
    };

    Array.from(element.childNodes).forEach((node) => {
      const result = processNode(node);
      if (result) elements.push(result);
    });

    return elements;
  }

  // SHA-256 of the rendered legal-body text. Tamper-evidence anchor: two
  // downloads of the same signed agreement reproduce the same hash; editing
  // the underlying admin template changes it. Computed before constructing
  // the PDF document so the certificate can render the value synchronously.
  const documentHash = await computeContentHash(
    `${documentTitle || ""}|${signedVersion || ""}|${contentElement?.textContent || ""}`
  );

  // Merge top-level signedDate/signedVersion (the legacy TOU/BAA caller
  // shape) into a synthetic auditRecord so the certificate gets a single
  // unified input. Caller-supplied auditRecord (PSA, future TOU/BAA) wins.
  const effectiveAudit = {
    signedAt: auditRecord?.signedAt || auditRecord?.signedDate || signedDate,
    ...auditRecord,
  };

  const SignedDocumentPDF = () => (
    <Document>
      <Page size="LETTER" style={styles.page} wrap>
        {parseHtmlToReactPdf(contentElement)}
        <SignatureCertificate
          documentTitle={documentTitle}
          documentVersion={signedVersion}
          documentHash={documentHash}
          signerName={signerName}
          signerEmail={signerEmail}
          signerTitle={signerTitle}
          organizationName={organizationName}
          auditRecord={effectiveAudit}
        />
      </Page>
    </Document>
  );

  const blob = await pdf(<SignedDocumentPDF />).toBlob();
  return deliverPdf({ blob, fileName, asBlob });
}

export async function baaToPdf({
  contentElement,
  baaType,
  signedDate,
  signedVersion,
  signerName,
  signerEmail,
  signerTitle,
  organizationName,
  auditRecord,
  asBlob,
}) {
  // Filename/title label. Derived from the variant key rather than a table
  // of every customer's legal name, which would put competitors' names in
  // each other's bundles. `lucas` is the platform's own document.
  const baaLabel = baaType === "lucas" ? "Lucas Health" : String(baaType || "").trim() || "Business Associate";
  const fileName = `${baaLabel.replace(/\s+/g, "_")}_BAA_${moment(signedDate).format("YYYYMMDD")}.pdf`;
  return documentToPdf({
    contentElement,
    fileName,
    signedDate,
    signedVersion,
    signerName,
    signerEmail,
    signerTitle,
    organizationName,
    documentTitle: `${baaLabel} Business Associate Agreement`,
    auditRecord,
    asBlob,
  });
}

export async function termsToPdf({
  contentElement,
  signedDate,
  signedVersion,
  signerName,
  signerEmail,
  signerTitle,
  organizationName,
  auditRecord,
  asBlob,
}) {
  const fileName = `Terms_of_Use_${moment(signedDate).format("YYYYMMDD")}.pdf`;
  return documentToPdf({
    contentElement,
    fileName,
    signedDate,
    signedVersion,
    signerName,
    signerEmail,
    signerTitle,
    organizationName,
    documentTitle: "Terms of Use",
    auditRecord,
    asBlob,
  });
}

// Reconstitution Calculator disclaimer PDF. Like TOU, it renders from a
// `contentElement` ref (the on-page <ReconDisclaimerText/>). The lightweight
// click-wrap captures no PIN/selfie, so we pass only signedAt in the audit
// record — the certificate then omits the Authentication block rather than
// printing misleading "No"s. IP / userAgent live in the stored record and the
// certificate's standard disclosure covers them.
export async function reconDisclaimerToPdf({
  contentElement,
  signedDate,
  signedVersion,
  signerName,
  signerEmail,
  signerTitle,
  organizationName,
  asBlob,
}) {
  const fileName = `Reconstitution_Calculator_Disclaimer_${moment(signedDate).format("YYYYMMDD")}.pdf`;
  return documentToPdf({
    contentElement,
    fileName,
    signedDate,
    signedVersion,
    signerName,
    signerEmail,
    signerTitle,
    organizationName,
    documentTitle: "Reconstitution Calculator — Terms of Use",
    auditRecord: { signedAt: signedDate },
    asBlob,
  });
}

// Resolve the Purchaser (the manufacturer this agreement is with) for a
// signed PSA, in order of trustworthiness: an explicit value from the
// caller, the frozen signature snapshot, then whatever the org's own
// server-resolved banking template says it calls itself.
//
// Returns null when nothing resolves, and the caller OMITS the row. It must
// never fall back to a bundled constant — that printed one manufacturer's
// legal name onto a competitor's signed contract.
export function resolvePsaPurchaserName({ purchaserName, snapshot, bankingInfo } = {}) {
  const candidates = [
    purchaserName,
    snapshot?.purchaserName,
    bankingInfo?.ach?.companyName,
    bankingInfo?.check?.companyName,
    bankingInfo?.ach?.beneficiaryName,
  ];
  const found = candidates.find((v) => typeof v === "string" && v.trim() !== "");
  return found ? found.trim() : null;
}

// Product Service Agreement PDF. Unlike BAA/TOU this one doesn't have a
// reusable `contentElement` on the user-side page, so the caller passes the
// resolved legal body in. Consumed by both the provider's own settings page
// and the manufacturer-side ProviderInfoDrawer Required Documents flow.
export async function psaToPdf({
  legalBody,
  signedDate,
  signerName,
  signerEmail,
  signerTitle,
  snapshot,
  organizationName,
  fieldValues,
  pricingSchedule,
  bankingInfo,
  purchaserName,
  auditRecord,
  asBlob,
}) {
  const styles = StyleSheet.create({
    page: { padding: 50, fontSize: 11, lineHeight: 1.5, fontFamily: "Helvetica" },
    h1: { fontSize: 22, fontFamily: "Helvetica-Bold", textAlign: "center", marginBottom: 20, marginTop: 10 },
    h2: { fontSize: 16, fontFamily: "Helvetica-Bold", marginTop: 20, marginBottom: 10 },
    h3: { fontSize: 13, fontFamily: "Helvetica-Bold", marginTop: 14, marginBottom: 6 },
    p: { marginBottom: 10, textAlign: "left" },
    bold: { fontFamily: "Helvetica-Bold" },
    table: { marginBottom: 12 },
    tableLead: { marginBottom: 4, fontFamily: "Helvetica-Bold" },
    tableRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: "#ccc", paddingVertical: 3 },
    tableHeaderRow: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#333", paddingVertical: 4 },
    tableCellHeader: { flex: 1, fontFamily: "Helvetica-Bold", fontSize: 10 },
    tableCell: { flex: 1, fontSize: 10 },
    tableLabel: { width: 160, fontFamily: "Helvetica-Bold", fontSize: 10 },
    tableValue: { flex: 1, fontSize: 10 },
    snapshot: {
      marginTop: 16,
      marginBottom: 24,
      padding: 16,
      backgroundColor: "#f4f7fb",
      borderTopWidth: 1,
      borderTopColor: "#1f4f8a",
      borderBottomWidth: 1,
      borderBottomColor: "#1f4f8a",
    },
    snapshotTitle: {
      fontSize: 11,
      fontFamily: "Helvetica-Bold",
      letterSpacing: 1.2,
      color: "#1f4f8a",
      marginBottom: 10,
      textAlign: "center",
    },
    snapshotRow: {
      flexDirection: "row",
      paddingVertical: 4,
      borderBottomWidth: 0.5,
      borderBottomColor: "#dbe2ee",
    },
    snapshotLabel: {
      width: 130,
      fontSize: 10,
      fontFamily: "Helvetica-Bold",
      color: "#33415c",
    },
    snapshotValue: { flex: 1, fontSize: 10, color: "#0f172a" },
    snapshotValueMono: { flex: 1, fontSize: 10, fontFamily: "Courier", color: "#0f172a" },
  });

  const resolvedPurchaserName = resolvePsaPurchaserName({ purchaserName, snapshot, bankingInfo });

  const renderSnapshot = () => {
    if (!snapshot || typeof snapshot !== "object") return null;
    // Mono columns for fixed-format identifiers — keeps NPI / Taxpayer ID
    // visually distinct from prose values like seller name and address.
    const MONO_LABELS = new Set(["Group NPI", "Taxpayer ID", "Effective Date", "Phone"]);
    const rows = [
      ["Seller", snapshot.sellerName],
      ["Address", snapshot.address],
      ["Phone", snapshot.phone],
      ["Group NPI", snapshot.npi],
      ["Taxpayer ID", snapshot.taxId],
      ["Effective Date", snapshot.effectiveDate],
      ["Purchaser", resolvedPurchaserName],
    ].filter(([, v]) => v);
    if (rows.length === 0) return null;
    return (
      <View style={styles.snapshot} wrap={false}>
        <Text style={styles.snapshotTitle}>PARTIES & TERMS (AS SIGNED)</Text>
        {rows.map(([label, value], i) => (
          <View style={[styles.snapshotRow, i === rows.length - 1 ? { borderBottomWidth: 0 } : null]} key={i}>
            <Text style={styles.snapshotLabel}>{label}</Text>
            <Text style={MONO_LABELS.has(label) ? styles.snapshotValueMono : styles.snapshotValue}>{String(value)}</Text>
          </View>
        ))}
      </View>
    );
  };

  // Token substitution: matches the on-screen `renderWithFills` /
  // `substituteTokensInHtml` behavior. Unknown tokens render as a blank
  // underscore so the PDF mirrors what the user sees in the accordion.
  // Snapshot fields fall back when fieldValues is sparse (the PSA modal
  // doesn't capture full NAF answers — Taxpayer ID lives in snapshot.taxId).
  const TOKEN_RE = /\{\{(FIELD:[^}]+|ORG_NAME|FACILITY_TYPE|FACILITY_OTHER)\}\}/g;
  const SNAPSHOT_FIELD_FALLBACKS = { undefined_3: "taxId" };
  const formatCents = (cents) => {
    if (cents == null) return "";
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const resolveToken = (token) => {
    if (token === "ORG_NAME") return organizationName || snapshot?.sellerName || null;
    if (token === "FACILITY_TYPE" || token === "FACILITY_OTHER") return null;
    const key = token.slice("FIELD:".length);
    let raw = fieldValues?.[key];
    if (raw == null || raw === "") {
      const fb = SNAPSHOT_FIELD_FALLBACKS[key];
      if (fb) raw = snapshot?.[fb];
    }
    return raw == null || raw === "" ? null : String(raw);
  };
  const substituteTokens = (text) => {
    if (!text || typeof text !== "string" || !text.includes("{{")) return text;
    return text.replace(TOKEN_RE, (_m, token) => resolveToken(token) ?? "______");
  };

  const renderPricingTable = (key) => {
    if (!isPricingScheduleConfigured(pricingSchedule)) return null;
    const feeYear = pricingSchedule.asOfYear ? `${pricingSchedule.asOfYear} ` : "";
    const feeSource = pricingSchedule.feeSource || "CMS";
    return (
      <View key={key} style={styles.table}>
        {pricingSchedule.categories.map((cat, ci) => (
          <View key={cat.id || cat.name || ci} style={{ marginBottom: 8 }}>
            <Text style={styles.tableLead}>{cat.name}</Text>
            <View style={styles.tableHeaderRow}>
              <Text style={styles.tableCellHeader}>Product</Text>
              <Text style={styles.tableCellHeader}>HCPCS</Text>
              <Text style={styles.tableCellHeader}>Price / Unit</Text>
              <Text style={styles.tableCellHeader}>{`${feeYear}${feeSource} Fee`}</Text>
            </View>
            {(cat.items || []).map((item, ii) => (
              <View key={ii} style={styles.tableRow}>
                <Text style={styles.tableCell}>{item.name}</Text>
                <Text style={styles.tableCell}>{item.hcpcs}</Text>
                <Text style={styles.tableCell}>{formatCents(item.priceCents)}</Text>
                <Text style={styles.tableCell}>{formatCents(item.cmsFeeCents)}</Text>
              </View>
            ))}
          </View>
        ))}
        {pricingSchedule.paymentTerms ? <Text style={{ ...styles.p, marginTop: 6 }}>{pricingSchedule.paymentTerms}</Text> : null}
      </View>
    );
  };

  const renderBankingTable = (key) => {
    const achRows = buildAchRows(bankingInfo);
    const checkRows = buildCheckRows(bankingInfo);
    if (achRows.length === 0 && checkRows.length === 0) return null;
    return (
      <View key={key} style={styles.table}>
        {achRows.length > 0 ? (
          <View>
            <Text style={styles.tableLead}>Please send ACH / Wire payments to:</Text>
            {achRows.map(([label, value], i) => (
              <View key={`a${i}`} style={styles.tableRow}>
                <Text style={styles.tableLabel}>{label}</Text>
                <Text style={styles.tableValue}>{String(value)}</Text>
              </View>
            ))}
          </View>
        ) : null}
        {checkRows.length > 0 ? (
          <View>
            <Text style={{ ...styles.tableLead, marginTop: 8 }}>Please send CHECK payments to:</Text>
            {checkRows.map(([label, value], i) => (
              <View key={`c${i}`} style={styles.tableRow}>
                <Text style={styles.tableLabel}>{label}</Text>
                <Text style={styles.tableValue}>{String(value)}</Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    );
  };

  // Each entry in LEGAL_BODY.PA is { heading, body: [string | {h} | {table}] }.
  const renderLegalBody = () => {
    if (!Array.isArray(legalBody)) return null;
    return legalBody.map((section, idx) => (
      <View key={idx}>
        <Text style={styles.h2}>{section.heading}</Text>
        {Array.isArray(section.body) &&
          section.body.map((para, pIdx) => {
            if (para && typeof para === "object") {
              if (para.h) {
                return (
                  <Text key={pIdx} style={styles.h3}>
                    {para.h}
                  </Text>
                );
              }
              if (para.table === "pricing-schedule") return renderPricingTable(`p-${idx}-${pIdx}`);
              if (para.table === "manufacturer-banking") return renderBankingTable(`b-${idx}-${pIdx}`);
              return null;
            }
            return (
              <Text key={pIdx} style={styles.p}>
                {substituteTokens(String(para))}
              </Text>
            );
          })}
      </View>
    ));
  };

  // SHA-256 over the legal body + parties snapshot. Anchors the certificate
  // to the exact text the signer agreed to; admin edits to the legal
  // template (or to the signed snapshot) change the hash on re-download.
  const hashInput = `Product Service Agreement|${organizationName || ""}|${JSON.stringify(snapshot || {})}|${JSON.stringify(legalBody || [])}`;
  const documentHash = await computeContentHash(hashInput);

  const effectiveAudit = {
    signedAt: auditRecord?.signedAt || auditRecord?.signedDate || signedDate,
    ...auditRecord,
  };

  const PsaDocument = () => (
    <Document>
      <Page size="LETTER" style={styles.page} wrap>
        <Text style={styles.h1}>Product Service Agreement</Text>
        {organizationName ? <Text style={{ ...styles.p, textAlign: "center", marginBottom: 20 }}>{organizationName}</Text> : null}
        {renderSnapshot()}
        {renderLegalBody()}
        <SignatureCertificate
          documentTitle="Product Service Agreement"
          documentHash={documentHash}
          signerName={signerName}
          signerEmail={signerEmail}
          signerTitle={signerTitle}
          organizationName={organizationName}
          auditRecord={effectiveAudit}
        />
      </Page>
    </Document>
  );

  const fileName = `Product_Service_Agreement_${moment(signedDate || new Date()).format("YYYYMMDD")}.pdf`;
  const blob = await pdf(<PsaDocument />).toBlob();
  return deliverPdf({ blob, fileName, asBlob });
}
